import {
  FolderSync,
  MoonStarIcon,
  Settings,
  Smartphone,
  SunIcon,
} from "lucide-react";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import AvatarText from "../reusables/AvatarText";
import { FullScreenActionButton } from "../reusables/FullScreenActionButton";
import { useTheme } from "../theme-provider";
import { Badge } from "../ui/badge";
import { Side } from "../ui/customChervonIcon";
import * as Dropdown from "../ui/dropdown-menu";
import { Switch } from "../ui/switch";

import LogOutImg from "@/assets/images/Logout.png";
import {
  XERO_ERROR_MESSAGES,
  XERO_INFO_MESSAGES,
} from "@/constants/xero.constants";
import {
  formatToCapitalizedWords,
  isCustomerPortal,
  isSubDomain,
  renderFullName,
} from "@/lib/utils";
import { workspaceApi } from "@/redux/api/workspaceQuery";
import {
  useLazyXeroAuthDisconnectQuery,
  useLazyXeroAuthQuery,
} from "@/redux/api/xeroQuery";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import { refetchWorkspaceData } from "@/redux/slices/userSlice";
import { RootState } from "@/redux/store";
import Icons from "@/utils/Icons";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type UserDropDownProps = {
  side?: Side;
  isInteracting: boolean;
  setInteracting: React.Dispatch<React.SetStateAction<boolean>>;
};

const UserDropdown = ({
  side,
  isInteracting,
  setInteracting,
}: UserDropDownProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const [getAuthenticated, { isLoading }] = useLazyXeroAuthQuery();
  const [xeroAuthDisconnect] = useLazyXeroAuthDisconnectQuery();
  const { theme, setTheme } = useTheme();
  const userData = useSelector((state: RootState) => state.user?.userData);

  const workspaceData = useSelector(
    (state: RootState) => state.user?.workspaceData,
  );

  const isZeroSynced = workspaceData?.isSyncedWithXero ?? false;

  const logOut = (): void => {
    // dispatch(refetchUserData());
    localStorage.clear();
    customToast.success("Logged out successfully");
    window.location.href = "/login";
  };

  const handleTheme = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void => {
    e.stopPropagation();
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleLogout = (): void => {
    dispatch(
      showAlertDialog({
        title: "Log Out?",
        desc: "Are you sure you want to logout?",
        btnName: "Logout",
        cancelName: "No, Cancel",
        btnAction: logOut,
        img: LogOutImg,
        imgSize: 140,
      }),
    );
  };

  const renderSyncBadge = (): JSX.Element => {
    if (isZeroSynced) {
      return <Badge variant={"successLight"}>Connected</Badge>;
    } else if (isLoading) {
      return <Badge>Loading...</Badge>;
    } else {
      return (
        <Badge variant={"destructive"} className='text-nowrap'>
          Not Connected
        </Badge>
      );
    }
  };

  const handleXeroSync = async (): Promise<void> => {
    try {
      if (isZeroSynced) {
        const res = await xeroAuthDisconnect("");
        validateResponse({
          response: res,
          onSuccess: () => {
            customToast.success(XERO_INFO_MESSAGES.XERO_DISCONNECTED);
          },
        });
        dispatch(refetchWorkspaceData());
        dispatch(workspaceApi.util.invalidateTags(["singleWorkspace"]));

        return;
      }

      const res = await getAuthenticated({
        redirectUrl: `${window.location.origin}`,
      });

      const redirectUrl = res.data?.redirectUrl;
      if (redirectUrl) {
        // For connection path, dispatch before redirect

        window.location.href = redirectUrl;
      } else {
        customToast.error(XERO_ERROR_MESSAGES.CONNECTION_FAILED);
        // If connection fails, still try to refresh data
      }
      dispatch(refetchWorkspaceData());
      dispatch(workspaceApi.util.invalidateTags(["singleWorkspace"]));
    } catch (error) {
      customToast.error(XERO_ERROR_MESSAGES.CONNECTION_FAILED);
    }
  };

  const parentButtonRef = useRef<HTMLDivElement>(null);

  return (
    <div className='select-none userinfo-container'>
      <Dropdown.DropdownMenu
        open={isInteracting}
        onOpenChange={() => setInteracting(!isInteracting)}
      >
        <Dropdown.DropdownMenuTrigger>
          <AvatarText
            src={userData?.image}
            name={renderFullName({
              params: {
                firstName: userData?.firstName ?? "-",
                middleName: userData?.middleName ?? "",
                lastName: userData?.lastName ?? "",
              },
            })}
            title={userData?.firstName}
            subtitle={formatToCapitalizedWords(
              (userData?.role as unknown as string) ?? "",
            )}
          />
        </Dropdown.DropdownMenuTrigger>
        <Dropdown.DropdownMenuContent
          side={side || "bottom"}
          className=' mr-8 mb-4  transition-all md:w-[300px] lg:w-[330px] 3xl:w-[360px]'
        >
          <Dropdown.DropdownMenuLabel className=''>
            <AvatarText
              src={userData?.image}
              name={renderFullName({
                params: {
                  firstName: userData?.firstName ?? "-",
                  middleName: userData?.middleName ?? "",
                  lastName: userData?.lastName ?? "",
                },
              })}
              title={userData?.firstName}
              subtitle={userData?.email ?? "-"}
            />
            <Dropdown.DropdownMenuSeparator />
          </Dropdown.DropdownMenuLabel>
          {!isCustomerPortal && (
            <>
              <Dropdown.DropdownMenuItem className='gap-4 p-3 text-aldgray'>
                <Settings size={20} strokeWidth={1.5} />
                <span>Account Settings</span>
              </Dropdown.DropdownMenuItem>
              <Dropdown.DropdownMenuItem className='gap-4 p-3 text-aldgray'>
                <Smartphone size={20} strokeWidth={1.5} />
                <span>Download Mobile App</span>
              </Dropdown.DropdownMenuItem>
              {isSubDomain && (
                <Dropdown.DropdownMenuItem
                  className='gap-4 p-3 cursor-pointer text-aldgray'
                  onClick={() => handleXeroSync()}
                >
                  <FolderSync size={20} strokeWidth={1.5} />
                  <span>
                    {isZeroSynced ? "Synced with Xero" : "Connect with Xero"}
                  </span>
                  {renderSyncBadge()}
                </Dropdown.DropdownMenuItem>
              )}
            </>
          )}
          <Dropdown.DropdownMenuItem className='flex justify-between gap-4 p-3 text-aldgray'>
            {theme == "dark" ? (
              <div className='flex gap-4'>
                <SunIcon size={20} strokeWidth={1.5} />
                <span>Light Mode</span>
              </div>
            ) : (
              <div className='flex gap-4'>
                <MoonStarIcon size={20} strokeWidth={1.5} />
                <span>Dark Mode</span>
              </div>
            )}
            <Switch
              checked={theme === "dark"}
              size='small'
              onClick={(e) => handleTheme(e)}
            />
          </Dropdown.DropdownMenuItem>
          <Dropdown.DropdownMenuItem ref={parentButtonRef}>
            <FullScreenActionButton onclickRef={parentButtonRef} />
          </Dropdown.DropdownMenuItem>
          <Dropdown.DropdownMenuSeparator />
          <Dropdown.DropdownMenuItem
            className='gap-4 p-3 text-aldgray'
            onClick={() => handleLogout()}
          >
            <Icons
              name='logout'
              size={20}
              color={theme === "dark" ? "white" : "#214090"}
            />
            <span
              className={`${theme === "dark" ? "text-white" : "text-primary"}  font-normal`}
            >
              Logout
            </span>
          </Dropdown.DropdownMenuItem>
        </Dropdown.DropdownMenuContent>
      </Dropdown.DropdownMenu>
    </div>
  );
};

export default UserDropdown;
